import { supabase } from '@/lib/supabaseClient';

/**
 * Respuesta de la Edge Function `contratar-plan`.
 *
 * Tres desenlaces posibles, mutuamente excluyentes:
 *  - `url`      → hay que pasar por Stripe Checkout (alta o cambio con pago).
 *  - `programado` → bajada aplazada al final del periodo ya pagado; `fechaCambio`
 *                 indica cuándo se hará efectiva.
 *  - `cambiado` → cambio aplicado ya sobre la suscripción existente. En ese caso
 *                 `enTrial` dice si se ha cobrado algo, y `trabajadoresCancelados`
 *                 / `plazasLiberadas` cuántas altas ha tenido que retirar el plan
 *                 nuevo por no cubrirlas.
 *
 * `error` llega cuando la función rechaza la operación por regla de negocio
 * (no por fallo técnico) — p. ej. hay una baja programada y hace falta
 * reactivar primero, que es lo que señala `requiereReactivar`.
 */
export type ContratarPlanResp = {
  url: string | null;
  cambiado?: boolean;
  programado?: boolean;
  fechaCambio?: string | null;
  error?: string;
  requiereReactivar?: boolean;
  trabajadoresCancelados?: number;
  plazasLiberadas?: number;
  enTrial?: boolean;
};

/**
 * Inicia la contratación (o cambio) de plan: llama a la Edge Function que
 * crea el Stripe Checkout Session con el id_precio_stripe del plan
 * elegido, y devuelve la URL para redirigir al usuario.
 *
 * Devuelve `null` solo ante un fallo técnico (red, 500, función caída). Los
 * rechazos de negocio vuelven como respuesta normal con `error` relleno, para
 * que la UI pueda dar un mensaje concreto en vez del genérico.
 */
export async function contratarPlan(
  idEmpresa: string,
  idPlan: string
): Promise<ContratarPlanResp | null> {
  const { data, error } = await supabase.functions.invoke('contratar-plan', {
    body: {
      id_empresa: idEmpresa,
      id_plan: idPlan,
      // Se manda siempre la URL real del entorno actual (localhost en
      // desarrollo, QA, o producción) — evita cualquier dominio
      // hardcodeado en el backend.
      success_url: `${window.location.origin}/panel-empresa/subscriptions?checkout=ok`,
      cancel_url: `${window.location.origin}/panel-empresa/subscriptions?checkout=cancelado`,
    },
  });

  if (error) {
    // invoke() solo rellena `data` en respuestas 2xx: si la función contesta
    // el rechazo de negocio con 4xx, el cuerpo viaja dentro del error y hay
    // que sacarlo de ahí, o el componente nunca vería `requiereReactivar`.
    const ctx = (error as { context?: Response & { status?: number } }).context;
    const status = ctx?.status;

    if (status && status >= 400 && status < 500) {
      const cuerpo = await ctx
        ?.clone?.()
        .json()
        .catch(() => null);

      if (cuerpo && typeof cuerpo === 'object' && 'error' in cuerpo) {
        return cuerpo as ContratarPlanResp;
      }
    }

    console.error('Error al iniciar la contratación del plan:', {
      message: error.message,
      name: error.name,
      status,
      error,
    });
    return null;
  }

  return data as ContratarPlanResp;
}

/**
 * Deshace una bajada de plan programada (la que crea contratarPlan con
 * una Subscription Schedule) antes de que se haga efectiva — el plan
 * actual se queda tal cual, sin ningún cambio pendiente.
 */
export async function cancelarCambioPlan(idEmpresa: string): Promise<{ ok: boolean }> {
  const { error } = await supabase.functions.invoke('cancelar-cambio-plan', {
    body: { id_empresa: idEmpresa },
  });

  if (error) {
    console.error('Error al deshacer el cambio de plan:', error);
    return { ok: false };
  }

  return { ok: true };
}

/**
 * Programa la cancelación del plan para el final del periodo ya pagado
 * (no corta el acceso al instante). Equivalente a apagar "Auto-renovación".
 */
export async function cancelarPlan(
  idEmpresa: string
): Promise<{ ok: boolean; fechaFin?: string | null }> {
  const { data, error } = await supabase.functions.invoke('cancelar-plan', {
    body: { id_empresa: idEmpresa },
  });

  if (error) {
    console.error('Error al cancelar el plan:', error);
    return { ok: false };
  }

  return { ok: true, fechaFin: data?.fechaFin ?? null };
}

/**
 * Deshace una cancelación programada del plan (vuelve a activar la
 * auto-renovación) antes de que llegue la fecha de cierre.
 */
export async function reactivarPlan(idEmpresa: string): Promise<{ ok: boolean }> {
  const { error } = await supabase.functions.invoke('reactivar-plan', {
    body: { id_empresa: idEmpresa },
  });

  if (error) {
    console.error('Error al reactivar el plan:', error);
    return { ok: false };
  }

  return { ok: true };
}